import { StudentSkillStates } from '../models/StudentSkillState';
import { TargetSkillTypeLabel } from '../models/TargetSkillType';
import { mapSkillStatus } from './mapSkillStatus';
import { ProgressBarValues } from '../models/Chart';
import { SkillStatesClassname, SkillStatesColor, SkillStatesKey, SkillStatesLabel } from '../models/SkillState';
import { UsersBySkillResults } from '../models/UsersBySkillResults';
import { sortProgressBarByOrder } from './sortCriteria';
import { ProgressBar, ProgressBarOrder, UsersInProgressBar } from '../models/ProgressBar';
import { Skill, SkillResult } from '../models/Skill';

export const progressBarMapValues = (elements: StudentSkillStates[], highAndMediumRelIds: string[]) => {
  const chartValues: { [key: string]: { [key: string]: number } } = {
    Passed: { Unit: 0, Support: 0 },
    'Almost There': { Unit: 0, Support: 0 },
    'Keep Learning': { Unit: 0, Support: 0 },
    'Not Started': { Unit: 0, Support: 0 },
    Review: { Unit: 0, Support: 0 },
    'Placed Out': { Unit: 0, Support: 0 },
  };

  let extensionPassed: number = 0;
  let additionalSupportPassed: number = 0;
  let total: number = 0;

  if (elements.length === 0)
    return {
      values: chartValues,
      extensionPassed,
      total,
      additionalSupportPassed,
    };

  elements.forEach(el => {
    const { status } = mapSkillStatus(el.skillSubstate?.id, el.skillAssessment?.score);
    if (el.targetSkillType) {
      if (el.targetSkillType === TargetSkillTypeLabel.UNIT) {
        el.targetSkillType && chartValues[String(status)][el.targetSkillType]++;
        total++;
      }
      if (el.targetSkillType === TargetSkillTypeLabel.SUPPORT) {
        if (highAndMediumRelIds.includes(el.skill.id)) {
          el.targetSkillType && chartValues[String(status)][el.targetSkillType]++;
          total++;
        } else {
          if (status === SkillStatesLabel.PASSED || status === SkillStatesLabel.PLACED_OUT) additionalSupportPassed++;
        }
      }
      if (el.targetSkillType === TargetSkillTypeLabel.EXTENSION) {
        if (status === SkillStatesLabel.PASSED || status === SkillStatesLabel.PLACED_OUT) extensionPassed++;
      }
    }
  });

  return {
    values: chartValues,
    extensionPassed,
    total,
    additionalSupportPassed,
  };
};

export const progressBarDashboard = (progressBarValues: ProgressBarValues) => {
  return [
    {
      label: SkillStatesLabel.DEMONSTRATED,
      color: SkillStatesColor.PASSED__PERFECT__PLACED_OUT,
      value: Math.round(
        ((progressBarValues.values[SkillStatesLabel.PASSED]['Unit'] +
          progressBarValues.values[SkillStatesLabel.PLACED_OUT]['Unit'] +
          progressBarValues.values[SkillStatesLabel.PASSED]['Support'] +
          progressBarValues.values[SkillStatesLabel.PLACED_OUT]['Support']) /
          progressBarValues.total) *
          100,
      ),
      className: 'c-placed-out',
      valuePerType: {
        Unit:
          progressBarValues.values[SkillStatesLabel.PASSED]['Unit'] +
          progressBarValues.values[SkillStatesLabel.PLACED_OUT]['Unit'],
        Support:
          progressBarValues.values[SkillStatesLabel.PASSED]['Support'] +
          progressBarValues.values[SkillStatesLabel.PLACED_OUT]['Support'],
      },
    },
    {
      label: SkillStatesLabel.ALMOST_THERE,
      color: SkillStatesColor.ALMOST_THERE,
      value: Math.round(
        ((progressBarValues.values[SkillStatesLabel.ALMOST_THERE]['Unit'] +
          progressBarValues.values[SkillStatesLabel.ALMOST_THERE]['Support']) /
          progressBarValues.total) *
          100,
      ),
      className: 'c-almost-there',
      valuePerType: {
        Unit: progressBarValues.values[SkillStatesLabel.ALMOST_THERE]['Unit'],
        Support: progressBarValues.values[SkillStatesLabel.ALMOST_THERE]['Support'],
      },
    },
    {
      label: SkillStatesLabel.REVIEW,
      color: SkillStatesColor.REVIEW,
      value: Math.round(
        ((progressBarValues.values[SkillStatesLabel.REVIEW]['Unit'] +
          progressBarValues.values[SkillStatesLabel.REVIEW]['Support']) /
          progressBarValues.total) *
          100,
      ),
      className: 'c-review',
      valuePerType: {
        Unit: progressBarValues.values[SkillStatesLabel.REVIEW]['Unit'],
        Support: progressBarValues.values[SkillStatesLabel.REVIEW]['Support'],
      },
    },
    {
      label: SkillStatesLabel.KEEP_LEARNING,
      color: SkillStatesColor.KEEP_LEARNING,
      value: Math.round(
        ((progressBarValues.values[SkillStatesLabel.KEEP_LEARNING]['Unit'] +
          progressBarValues.values[SkillStatesLabel.KEEP_LEARNING]['Support']) /
          progressBarValues.total) *
          100,
      ),
      className: 'c-keep-learning',
      valuePerType: {
        Unit: progressBarValues.values[SkillStatesLabel.KEEP_LEARNING]['Unit'],
        Support: progressBarValues.values[SkillStatesLabel.KEEP_LEARNING]['Support'],
      },
    },
    {
      label: SkillStatesLabel.NOT_STARTED,
      color: SkillStatesColor.NOT_STARTED,
      value: Math.round(
        ((progressBarValues.values[SkillStatesLabel.NOT_STARTED]['Unit'] +
          progressBarValues.values[SkillStatesLabel.NOT_STARTED]['Support']) /
          progressBarValues.total) *
          100,
      ),
      className: 'not-started',
      valuePerType: {
        Unit: progressBarValues.values[SkillStatesLabel.NOT_STARTED]['Unit'],
        Support: progressBarValues.values[SkillStatesLabel.NOT_STARTED]['Support'],
      },
    },
  ];
};

export const progressBarSingleUnit = (usersBySkillResults: UsersBySkillResults[]) => {
  let totalUsers = 0;
  usersBySkillResults.forEach(el => (totalUsers += el.value.length));
  const values: ProgressBar[] = [];
  const demonstratedArray = usersBySkillResults.filter(
    el => el.key === SkillStatesKey.PASSED || el.key === SkillStatesKey.PLACED_OUT,
  );

  if (demonstratedArray.length) {
    let demonstratedCount = 0;
    let demonstratedUsers: UsersInProgressBar[] = [];
    demonstratedArray.forEach(el => {
      demonstratedCount += el.value.length;
      demonstratedUsers = [...demonstratedUsers, ...el.value];
    });

    values.push({
      label: SkillStatesLabel.DEMONSTRATED,
      color: SkillStatesColor.PASSED__PERFECT__PLACED_OUT,
      value: demonstratedCount ? Math.round((demonstratedCount / totalUsers) * 100) : 0,
      className: SkillStatesClassname.DEMONSTRATED,
      order: ProgressBarOrder.DEMONSTRATED,
      valuePerType: { value: demonstratedCount },
      users: demonstratedUsers.length ? demonstratedUsers : [],
    });
  }

  usersBySkillResults.forEach(el => {
    const val = SkillStatesLabel[el.key as keyof typeof SkillStatesLabel];
    if (val !== SkillStatesLabel.PASSED && val !== SkillStatesLabel.PLACED_OUT) {
      values.push({
        label: val,
        color: SkillStatesColor[el.key as keyof typeof SkillStatesColor],
        value: el.value.length ? Math.round((el.value.length / totalUsers) * 100) : 0,
        className: SkillStatesClassname[el.key as keyof typeof SkillStatesClassname],
        order: ProgressBarOrder[el.key as keyof typeof ProgressBarOrder],
        valuePerType: { value: el.value.length ? el.value.length : 0 },
        users: el.value.length ? el.value : [],
      });
    }
  });

  return values.sort(sortProgressBarByOrder);
};

export const progressBarMapValuesStudentTab = (elements: StudentSkillStates[]) => {
  const chartValues: { [key: string]: { count: number; skills: Skill[] } } = {
    Passed: { count: 0, skills: [] },
    'Almost There': { count: 0, skills: [] },
    'Keep Learning': { count: 0, skills: [] },
    'Not Started': { count: 0, skills: [] },
    Review: { count: 0, skills: [] },
    'Placed Out': { count: 0, skills: [] },
  };

  let total: number = 0;

  if (elements.length === 0)
    return {
      values: chartValues,
      total,
    };

  elements.forEach(el => {
    const { status } = mapSkillStatus(el.skillSubstate?.id, el.skillAssessment?.score);
    chartValues[String(status)].count++;
    chartValues[String(status)].skills = [...chartValues[String(status)].skills, el.skill];
    total++;
  });

  return {
    values: chartValues,
    total,
  };
};

export const progressBarDashboardStudentTab = (
  progressBarValues: { key: SkillResult; value: number }[],
  studentId: string,
) => {
  const total = progressBarValues
    ?.map(val => val?.value)
    ?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  return [
    {
      label: SkillStatesLabel.DEMONSTRATED,
      color: SkillStatesColor.PASSED__PERFECT__PLACED_OUT,
      value:
        total > 0
          ? (((progressBarValues?.find(x => x?.key === SkillStatesKey?.PASSED)?.value ?? 0) +
              (progressBarValues?.find(x => x?.key === SkillStatesKey?.PLACED_OUT)?.value ?? 0)) /
              total) *
            100
          : 0,
      className: 'c-demonstrated',
      count:
        (progressBarValues?.find(x => x?.key === SkillStatesKey?.PASSED)?.value ?? 0) +
        (progressBarValues?.find(x => x?.key === SkillStatesKey?.PLACED_OUT)?.value ?? 0),
      studentId,
      key: SkillStatesKey?.DEMONSTRATED,
    },
    {
      label: SkillStatesLabel.ALMOST_THERE,
      color: SkillStatesColor.ALMOST_THERE,
      value:
        total > 0
          ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.ALMOST_THERE)?.value ?? 0) / total) * 100
          : 0,
      className: 'c-almost-there',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.ALMOST_THERE)?.value ?? 0,
      studentId,
      key: SkillStatesKey?.ALMOST_THERE,
    },
    {
      label: SkillStatesLabel.REVIEW,
      color: SkillStatesColor.REVIEW,
      value:
        total > 0 ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.REVIEW)?.value ?? 0) / total) * 100 : 0,
      className: 'c-review',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.REVIEW)?.value ?? 0,
      studentId,
      key: SkillStatesKey?.REVIEW,
    },
    {
      label: SkillStatesLabel.KEEP_LEARNING,
      color: SkillStatesColor.KEEP_LEARNING,
      value:
        total > 0
          ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.KEEP_LEARNING)?.value ?? 0) / total) * 100
          : 0,
      className: 'c-keep-learning',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.KEEP_LEARNING)?.value ?? 0,
      studentId,
      key: SkillStatesKey?.KEEP_LEARNING,
    },
    {
      label: SkillStatesLabel.NOT_STARTED,
      color: SkillStatesColor.NOT_STARTED,
      value:
        total > 0
          ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.NOT_STARTED)?.value ?? 0) / total) * 100
          : 0,
      className: 'not-started',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.NOT_STARTED)?.value ?? 0,
      studentId,
      key: SkillStatesKey?.NOT_STARTED,
    },
  ];
};

export const progressBarDashboardSkillTab = (
  progressBarValues: { key: SkillResult; value: number }[],
  skillId: string,
) => {
  const total = progressBarValues
    ?.map(val => val?.value)
    ?.reduce((accumulator, currentValue) => accumulator + currentValue, 0);

  return [
    {
      label: SkillStatesLabel.DEMONSTRATED,
      color: SkillStatesColor.PASSED__PERFECT__PLACED_OUT,
      value:
        total > 0
          ? (((progressBarValues?.find(x => x?.key === SkillStatesKey?.PASSED)?.value ?? 0) +
              (progressBarValues?.find(x => x?.key === SkillStatesKey?.PLACED_OUT)?.value ?? 0)) /
              total) *
            100
          : 0,
      className: 'c-demonstrated',
      count:
        (progressBarValues?.find(x => x?.key === SkillStatesKey?.PASSED)?.value ?? 0) +
        (progressBarValues?.find(x => x?.key === SkillStatesKey?.PLACED_OUT)?.value ?? 0),
      skillId,
      key: SkillStatesKey?.DEMONSTRATED,
    },
    {
      label: SkillStatesLabel.ALMOST_THERE,
      color: SkillStatesColor.ALMOST_THERE,
      value:
        total > 0
          ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.ALMOST_THERE)?.value ?? 0) / total) * 100
          : 0,
      className: 'c-almost-there',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.ALMOST_THERE)?.value ?? 0,
      skillId,
      key: SkillStatesKey?.ALMOST_THERE,
    },
    {
      label: SkillStatesLabel.REVIEW,
      color: SkillStatesColor.REVIEW,
      value:
        total > 0 ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.REVIEW)?.value ?? 0) / total) * 100 : 0,
      className: 'c-review',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.REVIEW)?.value ?? 0,
      skillId,
      key: SkillStatesKey?.REVIEW,
    },
    {
      label: SkillStatesLabel.KEEP_LEARNING,
      color: SkillStatesColor.KEEP_LEARNING,
      value:
        total > 0
          ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.KEEP_LEARNING)?.value ?? 0) / total) * 100
          : 0,
      className: 'c-keep-learning',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.KEEP_LEARNING)?.value ?? 0,
      skillId,
      key: SkillStatesKey?.KEEP_LEARNING,
    },
    {
      label: SkillStatesLabel.NOT_STARTED,
      color: SkillStatesColor.NOT_STARTED,
      value:
        total > 0
          ? ((progressBarValues?.find(x => x?.key === SkillStatesKey?.NOT_STARTED)?.value ?? 0) / total) * 100
          : 0,
      className: 'not-started',
      count: progressBarValues?.find(x => x?.key === SkillStatesKey?.NOT_STARTED)?.value ?? 0,
      skillId,
      key: SkillStatesKey?.NOT_STARTED,
    },
  ];
};
